import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api, { apiError as fmtApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, Copy, KeyRound, ArrowLeft, ArrowRight, Eye, EyeOff, Check } from "lucide-react";
import smb1001Badge from "@/assets/smb1001-gold.png";
import dashboardShot from "@/assets/screens/dashboard.png";

/**
 * 3-phase login preserved: credentials → enroll/verify → recovery.
 * Refresh: RoomMaster silhouette + RiskEye navy/cyan. Left = dark showcase,
 * right = clean white form. All data-testids preserved.
 */
export default function Login() {
  const { user, setSession } = useAuth();
  const nav = useNavigate();
  const [phase, setPhase] = useState("credentials");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [challenge, setChallenge] = useState(null);
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [recovery, setRecovery] = useState(null);

  if (user && phase === "credentials") return <Navigate to="/dashboard" replace />;

  const submitCreds = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setChallenge(data.challenge_token);
      if (data.enrollment_needed) {
        const s = await api.post("/auth/totp/setup", { challenge_token: data.challenge_token });
        setSetup(s.data); setPhase("enroll");
      } else setPhase("verify");
    } catch (e) { setErr(fmtApiError(e)); } finally { setBusy(false); }
  };
  const submitEnroll = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const { data } = await api.post("/auth/totp/confirm", { challenge_token: challenge, code });
      setSession(data); setRecovery(data.recovery_codes); setPhase("recovery");
    } catch (e) { setErr(fmtApiError(e)); } finally { setBusy(false); }
  };
  const submitVerify = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const { data } = await api.post("/auth/totp/verify", { challenge_token: challenge, code });
      setSession(data); toast.success("Welcome back"); nav("/dashboard");
    } catch (e) { setErr(fmtApiError(e)); } finally { setBusy(false); }
  };
  const finishRecovery = () => { toast.success(`Signed in — save your ${recovery.length} recovery codes now`); nav("/dashboard"); };

  return (
    <div className="rm-landing min-h-screen grid lg:grid-cols-[1.05fr_1fr]" data-testid="login-page">
      {/* Left: dark showcase */}
      <div className="hidden lg:flex relative overflow-hidden flex-col justify-between p-12 bg-[#0A1329] text-white">
        <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-riskeye-cyan/15 blur-3xl pointer-events-none" aria-hidden />
        <div className="absolute -bottom-40 -left-40 w-[460px] h-[460px] rounded-full bg-emerald-500/10 blur-3xl pointer-events-none" aria-hidden />
        <div className="absolute inset-0 opacity-[0.045] pointer-events-none"
             style={{ backgroundImage: "linear-gradient(rgba(255,255,255,.35) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.35) 1px, transparent 1px)", backgroundSize: "44px 44px" }} aria-hidden />

        <Link to="/" className="relative z-10 flex items-center gap-2.5" data-testid="login-brand">
          <div className="h-10 w-10 rounded-lg bg-riskeye-cyan flex items-center justify-center text-[#0A1329] font-bold text-base shadow-[0_0_16px_rgba(34,211,238,0.5)]">R</div>
          <div className="leading-tight">
            <div className="font-display font-bold text-[20px] tracking-tight text-white">RiskEye</div>
            <div className="text-[9.5px] font-mono uppercase tracking-[0.22em] text-slate-400">by seQure</div>
          </div>
        </Link>

        <div className="relative z-10 max-w-[440px]">
          <div className="inline-flex items-center gap-2 rounded-full border border-riskeye-cyan/30 bg-riskeye-cyan/10 px-3 py-1.5 text-[10.5px] font-mono uppercase tracking-[0.22em] text-riskeye-cyan mb-6">Sign-in · secure by design</div>
          <h1 className="font-display font-bold text-4xl lg:text-5xl leading-[1.05] tracking-tight text-white">
            Every risk in your business.<br />
            <span className="text-riskeye-cyan">One place. Everyone in the loop.</span>
          </h1>
          <blockquote className="mt-10 border-l-2 border-riskeye-cyan pl-5">
            <div className="text-lg text-slate-200 italic leading-snug">"The board pack format is so clean, one of our directors asked if we hired McKinsey to prepare it."</div>
            <div className="mt-3 text-[12px] font-mono text-slate-400">— Sarah Nguyen · VP Governance, Meridian Logistics</div>
          </blockquote>
          <div className="mt-8 rounded-2xl border border-white/10 bg-[#0F1A38]/70 p-2 shadow-2xl">
            <img src={dashboardShot} alt="RiskEye dashboard preview" className="w-full rounded-xl block" />
          </div>
        </div>

        <div className="relative z-10 space-y-4">
          <div className="flex items-center gap-3" data-testid="cert-badges">
            <Link to="/trust" className="flex items-center gap-2.5 rounded-2xl border border-white/10 bg-[#0F1A38]/70 px-3 py-2 hover:border-riskeye-cyan/40 transition-colors" data-testid="badge-smb1001">
              <img src={smb1001Badge} alt="SMB1001 Gold Level 3" className="h-10 w-auto" />
              <div className="leading-tight pr-1">
                <div className="text-[11px] font-mono font-semibold text-riskeye-cyan tracking-wider">SMB1001</div>
                <div className="text-[9px] font-mono uppercase tracking-[0.18em] text-slate-400">Gold · Level 3</div>
              </div>
            </Link>
            <Link to="/trust" className="flex items-center gap-2 rounded-2xl border border-emerald-400/25 bg-emerald-500/10 px-3 py-2 hover:border-emerald-400/50 transition-colors" data-testid="badge-cis">
              <div className="h-10 w-10 rounded-lg bg-emerald-500 flex flex-col items-center justify-center font-mono font-bold text-[#0A1329]">
                <div className="text-[10px] leading-none">CIS</div>
                <div className="text-[7px] mt-0.5 leading-none tracking-widest">CONTROLS</div>
              </div>
              <div className="leading-tight">
                <div className="text-[11px] font-mono font-semibold text-emerald-300 tracking-wider">CIS Controls</div>
                <div className="text-[9px] font-mono uppercase tracking-[0.18em] text-slate-400">Certified</div>
              </div>
            </Link>
          </div>
          <div className="flex items-center justify-between text-[10.5px] font-mono uppercase tracking-[0.24em] text-slate-500 pt-4 border-t border-white/10">
            <span className="flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-emerald-400" /> TLS · AES-256 · TOTP required</span>
            <Link to="/trust" className="hover:text-white">Trust →</Link>
          </div>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-6 md:p-12 bg-white">
        <div className="w-full max-w-[400px]">
          <Link to="/" className="lg:hidden flex items-center gap-2.5 mb-10">
            <div className="h-9 w-9 rounded-lg bg-riskeye-cyan flex items-center justify-center text-[#0A1329] font-bold text-sm">R</div>
            <div className="font-display font-bold text-xl text-[#0A1329]">RiskEye</div>
          </Link>

          {phase === "credentials" && (
            <form onSubmit={submitCreds} data-testid="login-form">
              <div className="mb-9">
                <div className="text-[10px] uppercase tracking-[0.28em] font-mono text-slate-400 mb-3">Secure sign-in</div>
                <h2 className="font-display font-bold text-4xl text-[#0A1329] tracking-tight leading-none">Welcome back.</h2>
                <p className="text-[14px] text-slate-500 mt-3">Enter your credentials to continue. Two-factor is required on every session.</p>
                <div className="mt-4 inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.2em] font-mono text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1" data-testid="login-2fa-enforced">
                  <ShieldCheck className="h-3.5 w-3.5" /> 2FA enforced · no opt-out
                </div>
              </div>
              <div className="space-y-5">
                <div>
                  <Label htmlFor="email" className="text-[12px] font-medium text-slate-700">Work email</Label>
                  <Input id="email" type="email" autoComplete="username" placeholder="you@company.com" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5 h-11 rounded-xl bg-white border-slate-200 focus:border-cyan-600 focus:ring-0" data-testid="login-email" />
                </div>
                <div>
                  <Label htmlFor="password" className="text-[12px] font-medium text-slate-700">Password</Label>
                  <div className="relative mt-1.5">
                    <Input id="password" type={showPw ? "text" : "password"} autoComplete="current-password" placeholder="••••••••••••" value={password} onChange={(e) => setPassword(e.target.value)} required className="h-11 pr-10 rounded-xl bg-white border-slate-200 focus:border-cyan-600 focus:ring-0" data-testid="login-password" />
                    <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700" tabIndex={-1} data-testid="login-password-toggle" aria-label={showPw ? "Hide password" : "Show password"}>
                      {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                {err && <div className="text-[13px] text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2.5" data-testid="login-error">{err}</div>}
                <Button type="submit" disabled={busy} className="w-full h-11 rounded-full bg-riskeye-cyan hover:bg-[#67E8F9] text-[#0A1329] font-semibold text-[14px] tracking-tight transition-colors shadow-[0_0_20px_rgba(34,211,238,0.3)]" data-testid="login-submit">
                  {busy ? "Signing in…" : "Sign in →"}
                </Button>
                <div className="flex items-center justify-between text-[12.5px]">
                  <a href="/forgot-password" className="text-slate-600 hover:text-cyan-700 font-medium underline-offset-4 hover:underline" data-testid="login-forgot-password">Forgot password?</a>
                  <a href="mailto:hello@riskeye.io" className="text-slate-500 hover:text-slate-900">Need help?</a>
                </div>
                <div className="pt-4 border-t border-slate-100 text-center">
                  <span className="flex items-center justify-center gap-1.5 text-slate-400 font-mono uppercase tracking-wider text-[10.5px]"><ShieldCheck className="h-3.5 w-3.5 text-emerald-500" /> Encrypted · SOC-ready</span>
                </div>
              </div>
            </form>
          )}

          {phase === "enroll" && setup && (
            <form onSubmit={submitEnroll} data-testid="enroll-form">
              <div className="mb-7">
                <div className="inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.28em] font-mono text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1 mb-4"><ShieldAlert className="h-3.5 w-3.5" /> Set up 2FA</div>
                <h2 className="font-display font-bold text-3xl text-[#0A1329] tracking-tight leading-tight">Scan the QR code.</h2>
                <p className="text-[13.5px] text-slate-500 mt-2">Use Google Authenticator, 1Password, Authy or any TOTP app — then enter the 6-digit code to finish.</p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 flex flex-col items-center">
                <img src={setup.qr_data_uri} alt="TOTP QR" className="w-48 h-48" data-testid="totp-qr" />
                <div className="mt-4 text-[10px] uppercase tracking-[0.24em] font-mono text-slate-400 font-semibold">Or enter manually</div>
                <div className="mt-1.5 font-mono text-[11px] tracking-wider text-[#0A1329] select-all break-all text-center max-w-[260px]" data-testid="totp-secret">{setup.secret}</div>
              </div>
              <div className="mt-5">
                <Label htmlFor="code" className="text-[12px] font-medium text-slate-700">6-digit code</Label>
                <Input id="code" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} maxLength={6} required className="mt-1.5 h-11 rounded-xl tracking-[0.6em] text-center font-mono text-lg bg-white border-slate-200 focus:border-cyan-600 focus:ring-0" data-testid="totp-code" />
              </div>
              {err && <div className="mt-3 text-[13px] text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2.5" data-testid="enroll-error">{err}</div>}
              <Button type="submit" disabled={busy || code.length !== 6} className="w-full mt-5 h-11 rounded-full bg-riskeye-cyan hover:bg-[#67E8F9] text-[#0A1329] font-semibold" data-testid="totp-confirm-submit">
                {busy ? "Verifying…" : "Enable 2FA & sign in"}
              </Button>
            </form>
          )}

          {phase === "verify" && (
            <form onSubmit={submitVerify} data-testid="verify-form">
              <div className="mb-7">
                <div className="inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.28em] font-mono text-cyan-700 bg-cyan-50 border border-cyan-200 rounded-full px-2.5 py-1 mb-4"><ShieldCheck className="h-3.5 w-3.5" /> Two-factor</div>
                <h2 className="font-display font-bold text-3xl text-[#0A1329] tracking-tight leading-tight">Enter your code.</h2>
                <p className="text-[13.5px] text-slate-500 mt-2">Enter the 6-digit code from your authenticator app — or a recovery code if you've lost your device.</p>
              </div>
              <div>
                <Label htmlFor="code" className="text-[12px] font-medium text-slate-700">Authentication code</Label>
                <Input id="code" inputMode="text" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value.replace(/[^\dA-Za-z]/g, "").slice(0, 10))} required className="mt-1.5 h-11 rounded-xl tracking-[0.4em] text-center font-mono text-lg bg-white border-slate-200 focus:border-cyan-600 focus:ring-0" data-testid="totp-verify-code" />
              </div>
              {err && <div className="mt-3 text-[13px] text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2.5" data-testid="verify-error">{err}</div>}
              <Button type="submit" disabled={busy || code.length < 6} className="w-full mt-5 h-11 rounded-full bg-riskeye-cyan hover:bg-[#67E8F9] text-[#0A1329] font-semibold" data-testid="totp-verify-submit">
                {busy ? "Verifying…" : "Verify & sign in →"}
              </Button>
              <button type="button" onClick={() => { setPhase("credentials"); setCode(""); setErr(""); }} className="mt-5 inline-flex items-center gap-1.5 text-[12px] text-slate-500 hover:text-slate-900 font-mono" data-testid="back-to-signin">
                <ArrowLeft className="h-3.5 w-3.5" /> back to sign-in
              </button>
            </form>
          )}

          {phase === "recovery" && recovery && (
            <div data-testid="recovery-panel">
              <div className="mb-6">
                <div className="inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.28em] font-mono text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1 mb-4"><KeyRound className="h-3.5 w-3.5" /> Save these codes</div>
                <h2 className="font-display font-bold text-3xl text-[#0A1329] tracking-tight leading-tight">Recovery codes.</h2>
                <p className="text-[13.5px] text-slate-500 mt-2">Each code can be used once if you lose your authenticator. Store them somewhere safe — this is the <span className="font-semibold text-[#0A1329]">only</span> time they will be shown.</p>
              </div>
              <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4">
                <div className="grid grid-cols-2 gap-2 font-mono text-[13px] text-[#0A1329]" data-testid="recovery-codes-list">
                  {recovery.map((c) => <div key={c} className="px-2 py-1.5 bg-white rounded border border-amber-200 text-center tracking-wider">{c}</div>)}
                </div>
              </div>
              <div className="flex gap-2 mt-5">
                <Button type="button" variant="outline" className="rounded-full border-slate-200" onClick={() => { navigator.clipboard.writeText(recovery.join("\n")); toast.success("Codes copied"); }} data-testid="recovery-copy"><Copy className="h-4 w-4 mr-1.5" /> Copy all</Button>
                <Button type="button" onClick={finishRecovery} className="flex-1 rounded-full bg-riskeye-cyan hover:bg-[#67E8F9] text-[#0A1329] font-semibold" data-testid="recovery-continue">I've saved them — continue →</Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
